import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { Button, Chip, Icon, SearchBar, Select, Toast } from "@legal-500/legal500-design-system";
import type { Column, ToastType } from "@legal-500/legal500-design-system";
import { PageHeader } from "@/components/PageHeader";
import { DataTable } from "@/components/DataTable/DataTable";
import { PaginationControls } from "@/components/PaginationControls";
import { ConfirmationModal } from "@/components/Modal/ConfirmationModal";
import EmptyState from "@/components/EmptyState";
import LoadingState from "@/components/LoadingState";
import { ErrorBanner } from "@/components/ErrorBanner/ErrorBanner";
import { debounce } from "@/utils";
import { useQuery, useMutation } from "@/hooks/useApi";
import {
  surveyLibrary,
  type LibraryQuestion,
  type QuestionType,
  type DisplayFilterRule,
  type SubmissionFilters,
  type CreateQuestionBody,
  type UpdateQuestionBody,
  getLocalizedTextDefault,
} from "@/api/survey-library.service";
import { SURVEY_QUESTION_TYPE_CONFIG } from "../questionTypes";
import { QuestionFormModal } from "./QuestionFormModal";
import styles from "./QuestionLibrary.module.css";

const DEFAULT_ITEMS_PER_PAGE = 10;

const FILTER_KEY_LABELS: Record<string, string> = {
  refereeBusinessType: "Business Type",
  submissionType: "Submission",
  refereeType: "Referee",
};

function renderDisplayRules(displayRules: DisplayFilterRule[]) {
  if (displayRules.length === 0) {
    return <span className={styles.mutedText}>All respondents</span>;
  }

  return (
    <div className={styles.rulesCell}>
      {displayRules.map((rule, ruleIdx) => {
        const filters = rule.submissionFilters;
        if (!filters) return null;

        const entries = Object.entries(filters).filter(([, value]) => value) as [
          keyof SubmissionFilters,
          string,
        ][];

        if (entries.length === 0) return null;

        return (
          <div key={ruleIdx} className={styles.rulePill}>
            {entries.map(([key, value], idx) => (
              <span key={key} className={styles.ruleTag}>
                {idx > 0 && <span className={styles.pillSeparator}>And</span>}
                <span className={styles.ruleTagLabel}>{FILTER_KEY_LABELS[key] ?? key}:</span>
                <span className={styles.ruleTagValue}>{value}</span>
              </span>
            ))}
          </div>
        );
      })}
    </div>
  );
}

function QuestionLibraryPage() {
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(DEFAULT_ITEMS_PER_PAGE);
  const [editingQuestion, setEditingQuestion] = useState<LibraryQuestion | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [deletingQuestion, setDeletingQuestion] = useState<LibraryQuestion | null>(null);
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null);

  const applyDebouncedSearchRef = useRef<(value: string) => void>((_value: string) => undefined);
  applyDebouncedSearchRef.current = (value: string) => {
    setDebouncedSearchTerm(value);
    setCurrentPage(prev => (prev === 1 ? prev : 1));
  };

  const debouncedSearchChangeRef = useRef(
    debounce((value: unknown) => {
      applyDebouncedSearchRef.current(String(value));
    }, 300),
  );
  const debouncedSearchChange = debouncedSearchChangeRef.current;

  useEffect(() => {
    return () => debouncedSearchChangeRef.current.cancel();
  }, []);

  const offset = (currentPage - 1) * itemsPerPage;

  const {
    data: listResponse,
    loading,
    error,
    refetch,
    isRefetching,
  } = useQuery(
    () =>
      surveyLibrary.list({
        search: debouncedSearchTerm || undefined,
        limit: itemsPerPage,
        offset,
      }),
    [debouncedSearchTerm, currentPage, itemsPerPage],
  );

  const { mutate: createQuestion } = useMutation((data: CreateQuestionBody) =>
    surveyLibrary.create(data),
  );

  const { mutate: updateQuestion } = useMutation((args: { id: string; data: UpdateQuestionBody }) =>
    surveyLibrary.update(args.id, args.data),
  );

  const { mutate: deleteQuestion } = useMutation((id: string) => surveyLibrary.remove(id));

  const questions = listResponse?.questions ?? [];
  const totalCount = listResponse?.total ?? 0;
  const totalPages = Math.ceil(totalCount / itemsPerPage);

  const handleEditClick = useCallback((question: LibraryQuestion) => {
    setEditingQuestion(question);
    setIsEditModalOpen(true);
  }, []);

  const handleAddClick = useCallback(() => {
    setEditingQuestion(null);
    setIsEditModalOpen(true);
  }, []);

  const handleSave = useCallback(
    async (data: CreateQuestionBody | UpdateQuestionBody, id?: string) => {
      if (id) {
        await updateQuestion({ id, data });
        setToast({ message: "Question updated.", type: "success" });
      } else {
        await createQuestion(data);
        setToast({ message: "Question added.", type: "success" });
      }
      refetch();
    },
    [updateQuestion, createQuestion, refetch],
  );

  const handleDeleteClick = useCallback((question: LibraryQuestion) => {
    setDeletingQuestion(question);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deletingQuestion) return;
    try {
      await deleteQuestion(deletingQuestion.id);
      setDeletingQuestion(null);
      setToast({ message: "Question deleted.", type: "success" });
      refetch();
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to delete question.";
      setToast({ message, type: "error" });
    }
  }, [deletingQuestion, deleteQuestion, refetch]);

  const handleEditClose = useCallback(() => {
    setIsEditModalOpen(false);
    setEditingQuestion(null);
  }, []);

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    debouncedSearchChange(value);
  };

  const handleSearchClear = () => {
    debouncedSearchChange.cancel();
    setSearchTerm("");
    setDebouncedSearchTerm("");
    setCurrentPage(1);
  };

  const columns: Column<LibraryQuestion>[] = useMemo(
    () => [
      {
        header: "Question",
        accessor: "text" as keyof LibraryQuestion,
        render: (_value: unknown, row: LibraryQuestion) => (
          <div className={styles.questionCell}>
            <span className={styles.questionText}>{getLocalizedTextDefault(row.text)}</span>
            {getLocalizedTextDefault(row.description).trim() ? (
              <span className={styles.questionDescription}>
                {getLocalizedTextDefault(row.description)}
              </span>
            ) : null}
            {row.required && (
              <div className={styles.questionMeta}>
                <Chip size="small" variant="outlined" color="error">
                  Required
                </Chip>
              </div>
            )}
          </div>
        ),
      },
      {
        header: "Type",
        accessor: "type" as keyof LibraryQuestion,
        render: (value: unknown) => {
          const config = SURVEY_QUESTION_TYPE_CONFIG[value as QuestionType];
          if (!config) return <span>{String(value)}</span>;
          return (
            <Chip size="small" color="default" className={styles.questionTypeChip}>
              {config.label}
            </Chip>
          );
        },
      },
      {
        header: "Display Rules",
        accessor: "displayRules" as keyof LibraryQuestion,
        render: (_value: unknown, row: LibraryQuestion) => renderDisplayRules(row.displayRules),
      },
      {
        header: "Actions",
        accessor: "id" as keyof LibraryQuestion,
        render: (_value: unknown, row: LibraryQuestion) => (
          <div className={styles.actionsCell}>
            <Button
              variant="outlined"
              size="small"
              className={styles.actionButton}
              aria-label="Edit question"
              onClick={() => handleEditClick(row)}
            >
              <Icon icon="edit" size="small" />
            </Button>
            <Button
              variant="outlined"
              color="error"
              size="small"
              className={styles.actionDeleteButton}
              aria-label="Delete question"
              onClick={() => handleDeleteClick(row)}
            >
              <Icon icon="delete" size="small" />
            </Button>
          </div>
        ),
      },
    ],
    [handleEditClick, handleDeleteClick],
  );

  return (
    <div className={styles.wrapper}>
      <PageHeader title="Question Library">
        <Button
          variant="contained"
          color="primary"
          onClick={handleAddClick}
          startIcon={<Icon icon="plus" size="small" />}
        >
          Add Question
        </Button>
      </PageHeader>

      <div className={styles.searchRow}>
        <div className={styles.searchBar}>
          <SearchBar
            value={searchTerm}
            onChange={handleSearchChange}
            onClear={handleSearchClear}
            placeholder="Search questions..."
          />
        </div>
        <div className={styles.rowsPerPageControl}>
          <label htmlFor="itemsPerPage" className={styles.rowsPerPageLabel}>
            Rows per page:
          </label>
          <Select
            id="itemsPerPage"
            value={itemsPerPage}
            onChange={e => {
              setItemsPerPage(Number(e.target.value));
              setCurrentPage(1);
            }}
            options={[
              { value: 10, label: "10" },
              { value: 25, label: "25" },
              { value: 50, label: "50" },
              { value: 100, label: "100" },
            ]}
            size="small"
          />
        </div>
      </div>

      {error && <ErrorBanner error={error.message} onRetry={refetch} loading={isRefetching} />}

      {loading && listResponse === null ? (
        <LoadingState message="Loading questions..." />
      ) : !error && questions.length === 0 ? (
        <EmptyState
          variant="empty"
          title="No questions found"
          message="No questions match your search criteria. Try a different search term."
        />
      ) : !error ? (
        <>
          <div className={styles.tableContainer}>
            <div className={styles.scopedTable}>
              <DataTable
                data={questions}
                columns={columns}
                emptyMessage="No questions found matching your criteria."
              />
            </div>
          </div>

          <PaginationControls
            currentPage={currentPage}
            totalPages={totalPages}
            startIndex={offset}
            endIndex={offset + questions.length}
            totalCount={totalCount}
            itemName="questions"
            onPageChange={setCurrentPage}
          />
        </>
      ) : null}

      <QuestionFormModal
        isOpen={isEditModalOpen}
        onClose={handleEditClose}
        question={editingQuestion}
        onSave={handleSave}
        onSaveError={message => setToast({ message, type: "error" })}
      />

      {deletingQuestion && (
        <ConfirmationModal
          size="small"
          title="Delete Question"
          message={`Are you sure you want to delete this question?`}
          warningMessage="This action cannot be undone. The question will be permanently removed from the library."
          backdropClick={false}
          affectedItems={[
            { id: deletingQuestion.id, name: getLocalizedTextDefault(deletingQuestion.text) },
          ]}
          confirmButtonText="Delete"
          confirmButtonColor="error"
          onClose={() => setDeletingQuestion(null)}
          onConfirm={handleDeleteConfirm}
        />
      )}

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          duration={toast.type === "error" ? 5000 : 3000}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
}

export default QuestionLibraryPage;
